import type {
  ClientAddress,
  ClientAddressDto,
  ClientAddressPeriod,
  ClientAddressSearchResult,
  ClientAddressSearchResultDto,
  ClientAddressUsage,
  ClientAddressUsageDto,
  ClientAddressUsageRowDto,
  ClientAddressUsageUpdate,
  CreateClientAddressPayload,
  CreateClientAddressPayloadDto,
} from '../types';

export function clientAddressFromDto(address: ClientAddressDto): ClientAddress {
  return {
    id: address.id,
    version: address.version,
    line1: address.line1,
    line2: address.line2,
    line3: address.line3,
    postcode: address.postcode,
    city: address.city,
    country: address.country,
  };
}

export function clientAddressUsageFromDto(
  usage: ClientAddressUsageDto,
): ClientAddressUsage {
  return {
    id: usage.id,
    addressType: usage.address_type,
    email: usage.email,
    start: usage.start,
    end: usage.end,
    isHeadOffice: usage.is_head_office,
    address: clientAddressFromDto(usage.address),
    otherClients: usage.other_clients,
    otherClientNames: usage.other_client_names,
  };
}

export function clientAddressSearchResultFromDto(
  result: ClientAddressSearchResultDto,
): ClientAddressSearchResult {
  return {
    ...clientAddressFromDto(result),
    groupUsageTypes: result.group_usage_types,
    groupEmail: result.group_email,
  };
}

export function createClientAddressPayloadToDto(
  payload: CreateClientAddressPayload,
): CreateClientAddressPayloadDto {
  const usage = {
    address_type: payload.addressType,
    ...(payload.email !== undefined ? { email: payload.email } : {}),
    ...(payload.start !== undefined ? { start: payload.start } : {}),
    ...(payload.isHeadOffice !== undefined
      ? { is_head_office: payload.isHeadOffice }
      : {}),
  };
  if (payload.address !== undefined) {
    return { ...usage, address: payload.address };
  }
  return {
    ...usage,
    line1: payload.line1,
    line2: payload.line2,
    line3: payload.line3,
    postcode: payload.postcode,
    city: payload.city,
    country: payload.country,
  };
}

export function clientAddressUsageUpdateToDto(
  usageId: number,
  update: ClientAddressUsageUpdate,
): ClientAddressUsageRowDto {
  const { correction } = update;
  return {
    id: usageId,
    ...(update.addressType !== undefined
      ? { address_type: update.addressType }
      : {}),
    ...(update.email !== undefined ? { email: update.email } : {}),
    ...(update.end !== undefined ? { end: update.end } : {}),
    ...(update.isHeadOffice !== undefined
      ? { is_head_office: update.isHeadOffice }
      : {}),
    ...(correction
      ? {
          ...(correction.line1 !== undefined
            ? { line1: correction.line1 }
            : {}),
          ...(correction.line2 !== undefined
            ? { line2: correction.line2 }
            : {}),
          ...(correction.line3 !== undefined
            ? { line3: correction.line3 }
            : {}),
          ...(correction.postcode !== undefined
            ? { postcode: correction.postcode }
            : {}),
          ...(correction.city !== undefined ? { city: correction.city } : {}),
          address_version: correction.addressVersion,
        }
      : {}),
  };
}

/** The commit section of the client page that holds a usage of a period. */
export function clientAddressSectionId(
  period: ClientAddressPeriod,
): 'addresses' | 'former_addresses' {
  return period === 'former' ? 'former_addresses' : 'addresses';
}

/* Server errors */

type ErrorRecord = Record<string, unknown>;

function isRecord(value: unknown): value is ErrorRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function errorData(error: unknown): ErrorRecord | null {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data;
  if (!isRecord(data)) {
    return null;
  }
  return isRecord(data.payload) ? data.payload : data;
}

/**
 * The errors of the one row an Edit Address panel or a head-office switch
 * saved through the client commit, keyed by the API field names (`line1`,
 * `address_version`, `non_field_errors`, `duplicate_address`, ...), so a
 * form can show them under its fields. Null for other errors.
 */
export function clientAddressUsageRowErrors(
  error: unknown,
  period: ClientAddressPeriod,
): ErrorRecord | null {
  const sections = errorData(error)?.sections;
  const section = isRecord(sections)
    ? sections[clientAddressSectionId(period)]
    : undefined;
  const usages = isRecord(section) ? section.usages : undefined;
  if (!Array.isArray(usages) || usages.length === 0) {
    return null;
  }
  // A list of texts concerns the section as a whole (e.g. two head offices).
  if (usages.every((item) => typeof item === 'string')) {
    return { non_field_errors: usages };
  }
  const [row] = usages;
  return isRecord(row) ? row : null;
}

/**
 * The id of the existing address an exact duplicate refers to, from the
 * error of creating or correcting an address; null if it is not one.
 * API errors carry texts only, so the rest of the address is not usable:
 * look the address up in the search results by this id.
 */
export function duplicateAddressIdFromError(
  error: unknown,
  period?: ClientAddressPeriod,
): number | null {
  const errors =
    period === undefined
      ? errorData(error)
      : clientAddressUsageRowErrors(error, period);
  const duplicate = errors?.duplicate_address;
  const id = isRecord(duplicate) ? Number(duplicate.id) : Number.NaN;
  return Number.isInteger(id) ? id : null;
}
